import { Map } from 'maplibre-gl';

/**
 * Utility for fading in and out MapLibre layers
 */
export class LayerFadeUtils {
  /**
   * Fades out a MapLibre layer over the specified duration
   * @param map The MapLibre map instance
   * @param layerId The ID of the layer to fade out
   * @param durationMs Duration of the fade animation in milliseconds
   * @returns A promise that resolves when the animation is complete
   */
  static fadeOut(map: Map, layerId: string, durationMs: number = 7000): Promise<void> {
    return new Promise((resolve) => {
      const startTime = performance.now();
      
      // Get the layer to determine its type
      const layer = map.getLayer(layerId);
      
      if (!layer) {
        console.error(`Layer "${layerId}" not found`);
        resolve();
        return;
      }
      
      // Determine the appropriate opacity property based on layer type
      let opacityProperty: string;
      
      switch (layer.type) {
        case 'fill':
          opacityProperty = 'fill-opacity';
          break;
        case 'line':
          opacityProperty = 'line-opacity';
          break;
        case 'circle':
          opacityProperty = 'circle-opacity';
          break;
        case 'raster':
          opacityProperty = 'raster-opacity';
          break;
        case 'symbol':
          opacityProperty = 'icon-opacity';
          // If the layer uses text, we would need additional handling
          break;
        case 'heatmap':
          opacityProperty = 'heatmap-opacity';
          break;
        case 'hillshade':
          opacityProperty = 'hillshade-opacity'; 
          break;
        default:
          console.warn(`Layer type "${layer.type}" may not support opacity animation`);
          resolve();
          return;
      }
      
      // Get initial opacity value or default to 1.0
      const initialOpacity = map.getPaintProperty(layerId, opacityProperty) as number || 1;
      
      // Animation function
      const animate = (timestamp: number): void => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / durationMs, 1);
        const newOpacity = initialOpacity * (1 - progress);
        
        map.setPaintProperty(layerId, opacityProperty, newOpacity);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Optional: hide the layer when fully transparent
          // map.setLayoutProperty(layerId, 'visibility', 'none');
          resolve();
        }
      };
      
      // Start the animation
      requestAnimationFrame(animate);
    });
  }

  /**
   * Fades in a MapLibre layer over the specified duration
   * @param map The MapLibre map instance
   * @param layerId The ID of the layer to fade in
   * @param durationMs Duration of the fade animation in milliseconds
   * @returns A promise that resolves when the animation is complete
   */
  static fadeIn(map: Map, layerId: string, durationMs: number = 7000): Promise<void> {
    return new Promise((resolve) => {
      const startTime = performance.now();
      
      // Get the layer to determine its type
      const layer = map.getLayer(layerId);
      
      if (!layer) {
        console.error(`Layer "${layerId}" not found`);
        resolve();
        return;
      }
      
      // Determine the appropriate opacity property based on layer type
      let opacityProperty: string;
      
      switch (layer.type) {
        case 'fill':
          opacityProperty = 'fill-opacity';
          break;
        case 'line':
          opacityProperty = 'line-opacity';
          break;
        case 'circle':
          opacityProperty = 'circle-opacity';
          break;
        case 'raster':
          opacityProperty = 'raster-opacity';
          break;
        case 'symbol':
          opacityProperty = 'icon-opacity';
          break;
        case 'heatmap':
          opacityProperty = 'heatmap-opacity';
          break;
        case 'hillshade':
          opacityProperty = 'hillshade-opacity';
          break;
        default:
          console.warn(`Layer type "${layer.type}" may not support opacity animation`);
          resolve();
          return;
      }
      
      // Set initial opacity to 0
      map.setPaintProperty(layerId, opacityProperty, 0);
      
      // Get target opacity value or default to 1.0
      const targetOpacity = map.getPaintProperty(layerId, opacityProperty) as number || 1.0;
      
      // Animation function
      const animate = (timestamp: number): void => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / durationMs, 1);
        const newOpacity = progress * targetOpacity;
        
        map.setPaintProperty(layerId, opacityProperty, newOpacity);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      
      // Start the animation
      requestAnimationFrame(animate);
    });
  }
}
